import React, { createContext, useContext, useEffect, useState } from 'react';
import { Documentation } from '@open-rpc/docs-react';
import InspectorPlugin from '../InspectorPlugin/InspectorPlugin';
import {
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import $RefParser from "@apidevtools/json-schema-ref-parser";

/**
 * Props for {@link OpenRpcWidget}.
 *
 * @public
 */
export interface OpenRpcWidgetProps {
  definition: string;
}

interface AuthContextValue {
  authToken: string;
}

export const AuthContext = createContext<AuthContextValue>({ authToken: '' });
export const useAuthContext = () => useContext(AuthContext);

//TODO import themes from library and pass it via reactJsonOptions
const useStyles = makeStyles(theme => {
  if (theme.palette.type === 'dark') {
    return {
      // https://github.com/uiwjs/react-json-view/blob/main/core/src/theme/dark.ts
      docsRoot: {
        '--w-rjv-font-family': 'monospace',
        '--w-rjv-color': '#0184a6',
        '--w-rjv-key-string': '#0184a6',
        '--w-rjv-background-color': '#202020',
        '--w-rjv-line-color': '#323232',
        '--w-rjv-arrow-color': 'var(--w-rjv-color)',
        '--w-rjv-edit-color': 'var(--w-rjv-color)',
        '--w-rjv-info-color': '#656565',
        '--w-rjv-update-color': '#ebcb8b',
        '--w-rjv-copied-color': '#0184a6',
        '--w-rjv-copied-success-color': '#28a745',

        '--w-rjv-curlybraces-color': '#1896b6',
        '--w-rjv-brackets-color': '#1896b6',
        '--w-rjv-quotes-color': 'var(--w-rjv-key-string)',
        '--w-rjv-quotes-string-color': 'var(--w-rjv-type-string-color)',

        '--w-rjv-type-string-color': '#cb4b16',
        '--w-rjv-type-int-color': '#268bd2',
        '--w-rjv-type-float-color': '#859900',
        '--w-rjv-type-bigint-color': '#268bd2',
        '--w-rjv-type-boolean-color': '#2aa198',
        '--w-rjv-type-date-color': '#586e75',
        '--w-rjv-type-url-color': '#649bd8',
        '--w-rjv-type-null-color': '#d33682',
        '--w-rjv-type-nan-color': '#076678',
        '--w-rjv-type-undefined-color': '#586e75',
      },
      authMeta: {
        color: theme.palette.text.secondary,
        fontSize: '0.85rem',
        marginBottom: theme.spacing(1),
      },
    };
  }
  // https://github.com/uiwjs/react-json-view/blob/main/core/src/theme/light.ts
  return {
    docsRoot: {
      '--w-rjv-font-family': 'monospace',
      '--w-rjv-color': '#002b36',
      '--w-rjv-key-string': '#002b36',
      '--w-rjv-background-color': '#ffffff',
      '--w-rjv-line-color': '#ebebeb',
      '--w-rjv-arrow-color': 'var(--w-rjv-color)',
      '--w-rjv-edit-color': 'var(--w-rjv-color)',
      '--w-rjv-info-color': '#0000004d',
      '--w-rjv-update-color': '#ebcb8b',
      '--w-rjv-copied-color': '#002b36',
      '--w-rjv-copied-success-color': '#28a745',

      '--w-rjv-curlybraces-color': '#236a7c',
      '--w-rjv-colon-color': '#002b36',
      '--w-rjv-brackets-color': '#236a7c',
      '--w-rjv-quotes-color': 'var(--w-rjv-key-string)',
      '--w-rjv-quotes-string-color': 'var(--w-rjv-type-string-color)',

      '--w-rjv-type-string-color': '#cb4b16',
      '--w-rjv-type-int-color': '#268bd2',
      '--w-rjv-type-float-color': '#859900',
      '--w-rjv-type-bigint-color': '#268bd2',
      '--w-rjv-type-boolean-color': '#2aa198',
      '--w-rjv-type-date-color': '#586e75',
      '--w-rjv-type-url-color': '#0969da',
      '--w-rjv-type-null-color': '#d33682',
      '--w-rjv-type-nan-color': '#859900',
      '--w-rjv-type-undefined-color': '#586e75',
    },
    authMeta: {
      color: theme.palette.text.secondary,
      fontSize: '0.85rem',
      marginBottom: theme.spacing(1),
    },
  };
});

/**
 * Displays an example.
 *
 * @remarks
 *
 * Longer descriptions should be put after the `@remarks` tag. That way the initial summary
 * will show up in the API docs overview section, while the longer description will only be
 * displayed on the page for the specific API.
 *
 * @public
 */
export function OpenRpcWidget(props: OpenRpcWidgetProps) {
  const [doc, setDoc] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const classes = useStyles();

  const [authToken, setAuthToken] = useState<string>(
    () => localStorage.getItem('open-rpc-auth-token') ?? '',
  );
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [tokenInput, setTokenInput] = useState('');

  const handleAuthOpen = () => {
    setTokenInput(authToken);
    setAuthDialogOpen(true);
  };

  const handleAuthSave = () => {
    setAuthToken(tokenInput);
    localStorage.setItem('open-rpc-auth-token', tokenInput);
    setAuthDialogOpen(false);
  };

  const handleAuthClear = () => {
    setTokenInput('');
  };

  useEffect(() => {
    const loadSpec = async () => {
      try {
        const def = props.definition;

        if (!def) throw new Error('No OpenRPC definition found');

        const spec = def.trim().startsWith('{')
          ? JSON.parse(def)
          : await (await fetch(def)).json();


        const dereferenced = await $RefParser.dereference(spec);
        setDoc(dereferenced);

      } catch (e) {
        console.error('Failed to load OpenRPC definition', e);
      } finally {
        setLoading(false);
      }
    };

    loadSpec();
  }, [props.definition]);

  if (loading) return <CircularProgress />;
  if (!doc)
    return <Typography color="error">Unable to render OpenRPC spec</Typography>;

  return (
    <AuthContext.Provider value={{ authToken }}>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Button
          onClick={handleAuthOpen}
          variant="outlined"
          size="small"
          color={authToken ? 'primary' : 'default'}
        >
          {authToken ? '🔒 Authorized' : '🔓 Authorize'}
        </Button>
      </Box>

      <Dialog
        open={authDialogOpen}
        onClose={() => setAuthDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Available authorizations</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 'bold' }}>
            x-auth-token (apiKey)
          </Typography>
          <Typography className={classes.authMeta}>
            Name: x-auth-token &nbsp;|&nbsp; In: header
          </Typography>
          <TextField
            label="Value"
            variant="outlined"
            fullWidth
            type="password"
            value={tokenInput}
            onChange={e => setTokenInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAuthSave(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAuthClear} color="default">
            Clear
          </Button>
          <Button onClick={() => setAuthDialogOpen(false)} color="default">
            Close
          </Button>
          <Button onClick={handleAuthSave} color="primary" variant="contained">
            Authorize
          </Button>
        </DialogActions>
      </Dialog>

      <div className={classes.docsRoot}>
        <Documentation schema={doc} methodPlugins={[InspectorPlugin]} />
      </div>
    </AuthContext.Provider>
  );
}

export const openRpcWidgetConfig = {
  type: 'openrpc',
  title: 'OpenRPC',
  component: (definition: string) => <OpenRpcWidget definition={definition} />,
};
